// ElevationViewer.jsx - Shared elevation utilities and components
import React from 'react';
import { Loader2, RotateCcw } from 'lucide-react';
import { isZoomAtDefault } from '@/utils/zoom';

/**
 * ElevationLoadingIndicator - Reusable loading indicator for elevation operations
 * Used across WindowElevationsSection, CWElevationsSection, SFElevationsSection
 */
export const ElevationLoadingIndicator = ({ 
  message, 
  progressPercent, 
  showProgress = true,
  variant = 'overlay' // 'overlay' | 'inline' | 'banner'
}) => {
  if (variant === 'overlay') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 shadow-xl max-w-sm w-full mx-4">
          <div className="flex items-center gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            <div className="flex-1">
              <p className="font-medium">{message}</p>
              {showProgress && progressPercent !== undefined && progressPercent > 0 && (
                <div className="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-500 transition-all duration-300"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'banner') {
    return (
      <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-md">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-red-600" />
          <span className="text-red-700 text-sm font-medium">{message}</span>
        </div>
        {showProgress && progressPercent > 0 && (
          <div className="w-full bg-red-200 rounded-full h-1.5 mt-2">
            <div 
              className="bg-red-600 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        )}
      </div>
    );
  }

  // inline variant
  return (
    <div className="flex items-center gap-2 py-2">
      <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
      <span className="text-sm text-gray-600">{message}</span>
      {showProgress && progressPercent > 0 && (
        <div className="flex-1 max-w-32 h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-blue-500 transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      )}
    </div>
  );
};

/**
 * Helper function to convert hex color to rgba
 * Used for marker styling
 */
export const hexToRgba = (hex, alpha = 1) => {
  if (!hex) return `rgba(57, 255, 20, ${alpha})`; // Default green
  
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return `rgba(57, 255, 20, ${alpha})`;
  
  return `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}, ${alpha})`;
};

/**
 * Helper function to get elevation image URL
 * Supports both Cloudinary and legacy image paths
 */
export const getElevationImageUrl = (elevation, size = 'large', apiUrl) => {
  if (!elevation) return null;
  
  // Cloudinary URLs
  if (elevation?.imageCloudinary?.urls) {
    return elevation.imageCloudinary.urls[size] || 
           elevation.imageCloudinary.urls.large || 
           elevation.imageCloudinary.urls.medium;
  }
  
  // Legacy image paths
  if (elevation?.imagePaths && elevation.imagePaths.length > 0) {
    const path = elevation.imagePaths[0];
    return path.startsWith('http') ? path : `${apiUrl}${path}`;
  }
  
  return null;
};

/**
 * MarkerBadge - Displays a marker on elevation image
 */
export const MarkerBadge = ({
  marker,
  label,
  color = '#39FF14',
  isActive = false,
  isDragging = false,
  onClick,
  onMouseDown,
  onDelete,
  canEdit = false,
  scale = 1
}) => {
  const bgColor = hexToRgba(color, 0.9);
  const borderColor = hexToRgba(color, 1);
  
  return (
    <div
      className={`absolute cursor-pointer transition-all duration-150 ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
      style={{
        left: `${marker.x}%`,
        top: `${marker.y}%`,
        transform: `translate(-50%, -50%) scale(${scale})`,
        zIndex: isActive ? 100 : 10
      }}
      onClick={onClick}
      onMouseDown={onMouseDown}
    >
      <div
        className={`px-2 py-1 rounded-full text-xs font-bold shadow-lg whitespace-nowrap ${isActive ? 'ring-2 ring-white ring-offset-2' : ''}`}
        style={{
          backgroundColor: bgColor,
          border: `2px solid ${borderColor}`,
          color: '#000'
        }}
      >
        {label}
        {canEdit && isActive && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.();
            }}
            className="ml-1 text-red-600 hover:text-red-800"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * ZoomControls - Zoom in/out/reset controls for elevation viewer
 */
export const ZoomControls = ({
  zoom,
  onZoomIn,
  onZoomOut,
  onReset,
  minZoom = 0.5,
  maxZoom = 3
}) => {
  return (
    <div className="flex items-center gap-1 bg-white/90 rounded-lg shadow px-2 py-1">
      <button
        onClick={onZoomOut}
        disabled={zoom <= minZoom}
        className="p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
        title="Zoom Out"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
        </svg>
      </button>
      <span className="text-xs font-medium min-w-[3rem] text-center">
        {Math.round(zoom * 100)}%
      </span>
      <button
        onClick={onZoomIn}
        disabled={zoom >= maxZoom}
        className="p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
        title="Zoom In"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      </button>
      <button
        onClick={onReset}
        disabled={isZoomAtDefault(zoom)}
        className="p-1 hover:bg-gray-100 rounded ml-1 disabled:opacity-50 disabled:cursor-not-allowed"
        title="Reset zoom to 100%"
        aria-label="Reset zoom to 100%"
        data-testid="zoom-controls-reset-zoom-btn"
      >
        <RotateCcw className="w-4 h-4" />
      </button>
    </div>
  );
};

export default ElevationLoadingIndicator;
